import React, { useEffect, useState } from "react";
import usePasskeys from "../hooks/usePasskeys";
import useEnv from "../hooks/useEnv";
import { defineMessages, injectIntl } from "react-intl";
import PropTypes from "prop-types";
import { Button, Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import useFormatter from "../hooks/useFormatter";

const messages = defineMessages({
    title: {
        id: "Luotettu kirjautuminen",
        defaultMessage: "Luotettu kirjautuminen"
    },
    toggleLabel: {
        id: "Käytä passkeys-kirjautumista tässä selaimessa",
        defaultMessage: "Käytä passkeys-kirjautumista tässä selaimessa"
    },
    infoText: {
        id: "Voit kirjautua sisään passkey-avaimilla (Face ID, sormenjälki tai laitteesi salasana). Turvallinen passkey tallennetaan laitteellesi tai pilvitilillesi (esim. iCloud, 1Password), jolloin voit kirjautua käyttämättä kertakäyttöistä sähköpostikoodia joka kerta.",
        defaultMessage: "Voit kirjautua sisään passkey-avaimilla (Face ID, sormenjälki tai laitteesi salasana). Turvallinen passkey tallennetaan laitteellesi tai pilvitilillesi (esim. iCloud, 1Password), jolloin voit kirjautua käyttämättä kertakäyttöistä sähköpostikoodia joka kerta."
    },
    remove: {
        id: "Poista",
        defaultMessage: "Poista"
    },
    createdAt: {
        id: "Luotu",
        defaultMessage: "Luotu"
    }
})

const Passkeys = (props) => {
    const { getPasskeys, deletePasskey, togglePasskeysEnabled, passkeysEnabled } = usePasskeys();
    const [passkeys, setPasskeys] = useState(null);
    const [loading, setLoading] = useState(false);
    const { isDev } = useEnv();
    const { formatDate, formatTime } = useFormatter();

    const lng = props.intl.formatMessage;

    useEffect(() => {
        const fetchPasskeys = async () => {
            setLoading(true);
            try {
                const keys = await getPasskeys();
                setPasskeys(keys);
            } catch (error) {
                if (isDev) {
                    console.error("Error fetching passkeys:", error);
                }
            } finally {
                setLoading(false);
            }
        }
        if (passkeys === null) {
            void fetchPasskeys();
        }
    }, [passkeys]);

    /**
     * Handle deletion of a passkey
     * @param id
     * @returns {Promise<void>}
     */
    const handleDelete = async (id) => {
        await deletePasskey(id);
        // Refresh the list after deletion
        setPasskeys(null);
    }

    /**
     * Handle toggling passkeys enabled state
     * @returns {Promise<void>}
     */
    const handleToggle = async () => {
        await togglePasskeysEnabled();
    }

    /**
     * Render a single passkey row
     * @param {object} passkey
     * @returns {React.JSX.Element}
     */
    const passkeyRow = (passkey) => (
        <div
            key={passkey.credentialId}
            className={`d-flex justify-content-between mt-1 mb-1 border-light border-bottom border-1 pb-1 pt-1`}
        >
            <div className='col-sm-8'>
                <div>{passkey.friendlyCredentialName} - {passkey.relyingPartyId}</div>
                <small className='text-light'>
                    {lng(messages.createdAt)}:&nbsp;
                    {formatDate(passkey.createdAt)} {formatTime(passkey.createdAt)}
                </small>
            </div>
            <div className='col-sm-4 justify-content-end d-flex'>
                <Button
                    variant='outline-secondary'
                    className='btn-circle'
                    title={lng(messages.remove)}
                    onClick={() => handleDelete(passkey.credentialId)}
                >
                    <FontAwesomeIcon icon={faTrashAlt} aria-hidden="true" />
                </Button>
            </div>
        </div>
    )

    return (
        <div>
            <h1>{lng(messages.title)}</h1>
            <p>{lng(messages.infoText)}</p>
            <Form.Group controlId="use-passkeys-toggle" className="mb-4">
                <Form.Check
                    type="switch"
                    id="use-passkeys-toggle"
                    label={lng(messages.toggleLabel)}
                    checked={passkeysEnabled}
                    onChange={handleToggle}
                />
            </Form.Group>
            {loading && <Spinner animation="border" size="lg" />}
            {passkeys?.map(passkey => passkeyRow(passkey))}
        </div>
    );
}

Passkeys.propTypes = {
    'intl': PropTypes.object.isRequired
};

export default injectIntl(Passkeys);
